import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  X,
  Loader2,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  User,
  Phone,
  Mail,
  Package,
  Link as LinkIcon,
  Building2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSuggestProductDrawer } from "@/contexts/SuggestProductDrawerContext";

const CATEGORIES = [
  "Consumables",
  "Instruments",
  "Endodontics",
  "Orthodontics",
  "Prosthodontics",
  "Implantology",
  "Handpieces",
  "Dental Equipment",
  "Laboratory",
  "Basic Dental Products",
  "Other",
];

const emptyForm = {
  name: "",
  phone: "",
  email: "",
  product: "",
  brand: "",
  category: "",
  reference_url: "",
  notes: "",
};

async function submitSuggestion(payload) {
  try {
    await fetch("/api/product-suggestions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch {
    /* offline / mock — mirror to localStorage below */
  }
  const key = "dyntra_product_suggestions";
  const list = JSON.parse(localStorage.getItem(key) || "[]");
  list.push({ ...payload, submitted_at: new Date().toISOString() });
  localStorage.setItem(key, JSON.stringify(list));
}

export default function SuggestProductDrawer() {
  const { open, prefill, closeSuggestProduct } = useSuggestProductDrawer();
  const { user } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState("idle");
  const productRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setStatus("idle");
    setForm({
      ...emptyForm,
      name: user?.name || user?.fullName || "",
      email: user?.email || "",
      phone: user?.phone || "",
      product: prefill?.product || prefill?.query || "",
      category: prefill?.category || "",
    });
    setTimeout(() => productRef.current?.focus(), 100);
  }, [open, user, prefill]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") closeSuggestProduct(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, closeSuggestProduct]);

  const submit = async (e) => {
    e.preventDefault();
    const product = form.product.trim();
    const name = form.name.trim();
    const contact = form.phone.trim() || form.email.trim();
    if (!product) {
      toast.error("Tell us the product name or a short description so we can source it.");
      return;
    }
    if (!name) {
      toast.error("Please share your name.");
      return;
    }
    if (!contact) {
      toast.error("Please share a phone or email so our team can reach you.");
      return;
    }
    setStatus("loading");
    try {
      await submitSuggestion({
        ...form,
        product,
        name,
        source: "suggest_product_drawer",
      });
      setStatus("success");
      toast.success("Thanks! Our sourcing team will get back within 24 hours.");
      setTimeout(() => closeSuggestProduct(), 1500);
    } catch {
      setStatus("idle");
      toast.error("Couldn't submit right now. Please try again.");
    }
  };

  return (
    <>
      <div
        data-testid="suggest-drawer-backdrop"
        onClick={closeSuggestProduct}
        aria-hidden={!open}
        className={`fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="suggest-drawer-title"
        data-testid="suggest-drawer"
        className={`fixed top-0 right-0 z-[100] h-full w-full sm:w-[460px] lg:w-[520px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <header className="relative shrink-0 bg-gradient-to-br from-[#361D82] via-[#4B2AAD] to-[#6B4CD9] text-white px-6 py-5">
          <button
            type="button"
            data-testid="suggest-drawer-close"
            onClick={closeSuggestProduct}
            aria-label="Close suggestion form"
            className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.24em] text-white/75">
            <Sparkles className="h-3.5 w-3.5" /> Missing from our catalogue?
          </div>
          <h2 id="suggest-drawer-title" className="font-display text-2xl font-extrabold mt-1">Suggest a Product</h2>
          <p className="text-sm text-white/85 mt-1">
            Share the product you're looking for — our sourcing team gets back within 24 hours.
          </p>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <form id="suggest-form" onSubmit={submit} className="space-y-5">
            <FieldGroup title="What do you need?">
              <Field
                label="Product name / description"
                required
                testid="suggest-product"
                icon={Package}
                value={form.product}
                onChange={(v) => set("product", v)}
                inputRef={productRef}
                placeholder="e.g. GC Fuji IX GP Extra Powder & Liquid Kit"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                  label="Brand"
                  testid="suggest-brand"
                  icon={Building2}
                  value={form.brand}
                  onChange={(v) => set("brand", v)}
                  placeholder="e.g. GC, 3M, Ivoclar"
                />
                <SelectField
                  label="Category"
                  testid="suggest-category"
                  value={form.category}
                  onChange={(v) => set("category", v)}
                  options={[{ value: "", label: "Select category…" }, ...CATEGORIES.map((c) => ({ value: c, label: c }))]}
                />
              </div>
              <Field
                label="Reference link (optional)"
                testid="suggest-ref-url"
                icon={LinkIcon}
                type="url"
                value={form.reference_url}
                onChange={(v) => set("reference_url", v)}
                placeholder="https://…  (link to product page or datasheet)"
              />
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Additional notes</label>
                <textarea
                  data-testid="suggest-notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  placeholder="Pack size, shade, model number, or anything else that helps us match the right SKU."
                  className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-4 py-2.5 outline-none text-sm resize-none"
                />
              </div>
            </FieldGroup>

            <FieldGroup title="How can we reach you?">
              <Field
                label="Full name"
                required
                testid="suggest-name"
                icon={User}
                value={form.name}
                onChange={(v) => set("name", v)}
                placeholder="Dr. Priya Sharma"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                  label="Phone"
                  testid="suggest-phone"
                  icon={Phone}
                  type="tel"
                  value={form.phone}
                  onChange={(v) => set("phone", v)}
                  placeholder="+91 98xxxxxx00"
                />
                <Field
                  label="Email"
                  testid="suggest-email"
                  icon={Mail}
                  type="email"
                  value={form.email}
                  onChange={(v) => set("email", v)}
                  placeholder="you@clinic.com"
                />
              </div>
              <p className="text-[11px] text-gray-500">
                Provide at least one — phone is fastest for confirmation, email for sending you a link to the SKU once it's live.
              </p>
            </FieldGroup>
          </form>
        </div>

        <footer className="shrink-0 border-t border-gray-100 bg-white px-6 py-4">
          {status === "success" ? (
            <div
              data-testid="suggest-success"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-50 text-emerald-700 font-semibold text-sm py-3 border border-emerald-200"
            >
              <CheckCircle2 className="h-5 w-5" /> Suggestion received — closing…
            </div>
          ) : (
            <button
              type="submit"
              form="suggest-form"
              data-testid="suggest-submit"
              disabled={status === "loading"}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#4B2AAD] hover:bg-[#361D82] disabled:bg-[#4B2AAD]/70 text-white text-base font-bold py-3.5 transition-colors active:scale-[0.99]"
            >
              {status === "loading" ? (
                <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</>
              ) : (
                <>Submit Suggestion <ArrowRight className="h-5 w-5" /></>
              )}
            </button>
          )}
          <p className="mt-2 text-center text-[11px] text-gray-500">
            We reply within <span className="font-semibold text-gray-700">24 hours</span> with sourcing options or a comparable SKU.
          </p>
        </footer>
      </aside>
    </>
  );
}

function FieldGroup({ title, children }) {
  return (
    <fieldset className="space-y-3">
      <legend className="text-xs font-bold uppercase tracking-[0.18em] text-[#4B2AAD]">{title}</legend>
      {children}
    </fieldset>
  );
}

function Field({ label, testid, icon: Icon, value, onChange, type = "text", placeholder, required, inputRef }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-gray-500">
        {label}{required ? " *" : ""}
      </label>
      <div className="relative mt-1">
        {Icon && (
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400">
            <Icon className="h-4 w-4" />
          </span>
        )}
        <input
          ref={inputRef}
          data-testid={testid}
          type={type}
          value={value}
          required={required}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 ${
            Icon ? "pl-9 pr-3" : "px-3"
          } py-2.5 outline-none text-sm bg-white`}
        />
      </div>
    </div>
  );
}

function SelectField({ label, testid, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-3 py-2.5 outline-none text-sm bg-white"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
